import { Button, Card, Form, Row, Radio, Space } from "antd";
import { useCallback, useEffect, useState } from "react";
import isNil from "lodash/isNil";
import { useHistory, useParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { SRSApplicantType } from "utils/Types";
import { ratingScores } from "utils/AssetsData";
import { convertRatingObjToArray, deepEqual, excludeEmptyObj } from "utils/hooks/handleObjAndArray";
import { requiredRule } from "utils/validators/rules";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import api from "ApiClient";
import { ApiURL, AppURL } from "utils/Actions";
import { API_RESPONSE_STATUS } from "utils/Constants";
import { addRatedStudent, setScoreAndValue } from "redux/reducers/RatingsSlice";
import { showSuccessNotification } from "utils/hooks/handleNotification";
import CustomPrompt from "components/common/custom-prompt";
import SubmitModal from "../modals/SubmitModal";
import style from "./style.module.css";
import { isValidEnv } from "redux/reducers/GlobalUiSlice";

interface UACRatingFormProps {
  handleError: (error: any) => void;
  backToApplicationTable: () => void;
}

export default function UACRatingForm({ handleError, backToApplicationTable }: UACRatingFormProps) {
  const { env } = useAppSelector(state => state.globalUI);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { refNum: studentRefNumParam } = useParams<{ refNum: string }>();
  const { tableData: tableDataInRater } = useAppSelector(state => state.srsApplicants);

  const { topics, initialValue } = useAppSelector(state => state.ratings);

  const selectedStudent = tableDataInRater.find(
    (e: SRSApplicantType) => e.refNum.toString() === studentRefNumParam
  );
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isEqual, setIsEqual] = useState(true);
  const [disableDraft, setDisableDraft] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setDisableDraft(isEmpty(initialValue));
  }, [initialValue]);

  const onChange = () => {
    const values = form.getFieldsValue();
    const cleanedValues = excludeEmptyObj(values);
    const resultDeepEqual = deepEqual(cleanedValues, initialValue);
    setIsEqual(resultDeepEqual);
    setDisableDraft(isEmpty(cleanedValues));
  };

  const onSubmit = async (isSubmitBtn: boolean) => {
    setIsEqual(true);
    setIsSubmit(isSubmitBtn);
    isSubmitBtn && setIsSubmitting(true);
    !isSubmitBtn && setIsDraftLoading(true);

    if (!selectedStudent) {
      return false;
    }

    if (!isValidEnv(env)) {
      return;
    }

    form.validateFields().then(async values => {
      const scores = convertRatingObjToArray(values);
      if (!scores || scores?.length == 0) {
        !isSubmitBtn && setIsDraftLoading(false);
        return false;
      }
      const newScoreBody = {
        ratings: {
          refNum: Number(selectedStudent.refNum),
          draft: !isSubmitBtn,
          qualRatings: scores
        }
      };

      try {
        const res = await api.post(ApiURL.ratingScore, newScoreBody, {
          params: {
            source: env
          }
        });
        const { status } = await res?.data;
        if (status === API_RESPONSE_STATUS.SUCCESS) {
          dispatch(setScoreAndValue(newScoreBody.ratings.qualRatings));
          setIsEqual(true);
          if (isSubmitBtn) {
            dispatch(addRatedStudent(selectedStudent));
          } else {
            showSuccessNotification({ message: "Rating saved" });
          }
          backToApplicationTable();
        }
      } catch (error: any) {
        handleError(error);
        return false;
      } finally {
        setIsDraftLoading(false);
      }
    });
  };

  const handleClickSubmitButton = async () => {
    setIsSubmit(true);
    const valid = await validateForm();
    if (valid) {
      setIsSubmitModalOpen(true);
    }
  };

  const validateForm = async () => {
    const valid = await form.validateFields().then(async values => {
      const isPopulated = Object.values(values).every(item => !isNil(item));
      if (isPopulated) {
        return true;
      } else {
        return false;
      }
    });
    return valid;
  };

  const handleSaveChanges = useCallback(async () => {
    await onSubmit(false);
  }, [onSubmit]);

  return (
    <>
      <SubmitModal
        isopen={isSubmitModalOpen}
        onclose={() => setIsSubmitModalOpen(false)}
        backToApplication={backToApplicationTable}
        handleSubmit={() => onSubmit(true)}
        isSubmitting={isSubmitting}
        isSubmitError={false}
      />
      <CustomPrompt
        when={!isEqual && !isSubmit}
        navigate={path => history.push(path)}
        shouldBlockNavigation={location => {
          if (!isEqual && !isSubmit) {
            if (!location.toString().startsWith(AppURL.school.rater.ratings)) {
              return true;
            }
          }
          return false;
        }}
        onSaveChanges={handleSaveChanges}
      />
      <Form
        className="rating-form"
        form={form}
        initialValues={initialValue}
        onFieldsChange={onChange}
        layout="vertical"
        scrollToFirstError={true}
      >
        {topics.map((level: any, i: number) => {
          return (
            <Card style={{ marginTop: "2rem" }} key={level.elementId}>
              <fieldset role="radiogroup" aria-labelledby={`group-label-${i}`}>
                <Form.Item
                  label={
                    <legend id={`group-label-${i}`} className={style["radio-label"]}>
                      {level.topic}
                    </legend>
                  }
                  name={level.elementId}
                  required
                  rules={isSubmit ? [requiredRule] : []}
                >
                  <Radio.Group name={level.elementId}>
                    <Space direction="vertical">
                      {ratingScores.map((score: any) => (
                        <Radio
                          key={`${level.elementId}-${score.value}`}
                          id={`${level.elementId}-${score.value}`}
                          value={score.value}
                          name={level.elementId}
                        >
                          <label htmlFor={`${level.elementId}-${score.value}`}>{score.label}</label>
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </fieldset>
            </Card>
          );
        })}

        <Row style={{ marginTop: "3rem" }}>
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginRight: "2rem" }}
            onClick={handleClickSubmitButton}
          >
            {t("rating.submitBtn")}
          </Button>
          <Button loading={isDraftLoading} onClick={() => onSubmit(false)} disabled={disableDraft}>
            {t("rating.saveBtn")}
          </Button>
        </Row>
      </Form>
    </>
  );
}
